import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpResponse
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { Observable, of, OperatorFunction, throwError, timer } from 'rxjs';
import {
  catchError,
  concatMap,
  mergeMap,
  map,
  skipWhile,
  take,
  retry
} from 'rxjs/operators';

export interface HttpOptions {
  headers: HttpHeaders;
  params?: any;
  observe?: any;
  responseType?: string;
  body?: any;
}

export interface HttpAttachmentOptions {
  headers: { filename: string };
}

export const SCHEMA_VERSION = {
  OPPORTUNITY: 51,
  LEAD: 9,
  INDIVIDUAL: 8,
  COMPANY: 4,
  TRUST: 4
};

@Injectable({
  providedIn: 'root'
})
export class ApiHttp {
  public static getStandardOptions = (): HttpOptions => ({
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  });

  public static getStandardOptionsWithResponse = (): HttpOptions => ({
    ...ApiHttp.getStandardOptions(),
    observe: 'response' as const
  });

  public static getStandardOptionsWithMetadata = (): HttpOptions => ({
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    }),
    params: {
      metadata: true
    }
  });

  private static getStandardOptionsWithTextResponse = (): HttpOptions => ({
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    }),
    responseType: 'text'
  });

  public static calculationRequiredStatusCode: number = 250;
  public static calculationInProgressStatusCode: number = 256;

  constructor(protected httpClient: HttpClient) {}

  public download(url: string): Observable<Blob> {
    return this.httpClient.get(url, { responseType: 'blob' });
  }

  public downloadPost(url: string, body: unknown): Observable<Blob> {
    return this.httpClient.post(url, body, { responseType: 'blob' });
  }

  private getRequest<T>(
    url: string,
    entitySchemaVersion: number | null,
    options = ApiHttp.getStandardOptions()
  ): Observable<T> {
    let mergedOptions = this.mergeOptions(entitySchemaVersion, options);
    return this.httpClient.get<T>(url, mergedOptions).pipe(
      catchError((err) => {
        console.error(
          'An error occurred with the GET request: ' + JSON.stringify(err)
        );
        return throwError(() => err);
      }),
      retry({
        count: environment.retryConfig.maxRetries,
        delay: (error: HttpErrorResponse, retryCount: number) => {
          switch (error.status) {
            case 504:
              return timer(environment.retryConfig.delayMs);

            default:
              return throwError(() => error);
          }
        },
        resetOnSuccess: true
      })
    );
  }

  public getEntity<T>(
    host: string,
    path: string,
    entitySchemaVersion: number,
    options: HttpOptions = ApiHttp.getStandardOptions()
  ): Observable<T> {
    return this.getRequest(this.url(host, path), entitySchemaVersion, options);
  }

  public get<T>(
    host: string,
    path: string,
    options: HttpOptions = ApiHttp.getStandardOptions()
  ): Observable<T> {
    return this.getRequest(this.url(host, path), null, options);
  }

  public getText(
    host: string,
    path: string,
    entitySchemaVersion: number,
    options: HttpOptions = ApiHttp.getStandardOptionsWithTextResponse()
  ): Observable<string> {
    return this.getRequest(this.url(host, path), entitySchemaVersion, options);
  }

  public pollWhile<T>(
    pollCondition: (response: T) => boolean
  ): OperatorFunction<T, T> {
    return (source: Observable<T>) => {
      return timer(0, 500).pipe(
        concatMap(() => source),
        skipWhile((r) => pollCondition(r)),
        take(1)
      );
    };
  }

  public unwrapResponse<T>(
    response: Observable<HttpResponse<T>>
  ): Observable<T> {
    return response.pipe(map((r) => r.body!));
  }

  public tryWithAlternate<T>(
    primary: Observable<HttpResponse<T>>,
    secondary: Observable<HttpResponse<T>>,
    redirectPredicate: (response: HttpResponse<T>) => boolean
  ): Observable<HttpResponse<T>> {
    return primary.pipe(
      mergeMap((response: HttpResponse<T>) => {
        if (redirectPredicate(response)) {
          return secondary;
        } else {
          return of(response);
        }
      })
    );
  }

  public getWithMetadata<T>(
    host: string,
    path: string,
    entitySchemaVersion: number
  ): Observable<T> {
    const url = this.url(host, path);
    return this.httpClient.get<T>(
      url,
      this.mergeOptions(
        entitySchemaVersion,
        ApiHttp.getStandardOptionsWithMetadata()
      )
    );
  }

  private postRequest<T>(
    url: string,
    body: any,
    entitySchemaVersion: number | null,
    options?: HttpOptions | HttpAttachmentOptions
  ): Observable<T> {
    return this.httpClient
      .post<T>(url, body, this.mergeOptions(entitySchemaVersion, options))
      .pipe(
        catchError((err) => {
          console.error(
            'An error occurred with the POST request: ' + JSON.stringify(err)
          );
          return throwError(() => err);
        })
      );
  }

  public postEntity<T>(
    host: string,
    path: string,
    body: any,
    entitySchemaVersion: number,
    options?: HttpOptions | HttpAttachmentOptions
  ): Observable<T> {
    return this.postRequest(
      this.url(host, path),
      body,
      entitySchemaVersion,
      options
    );
  }

  public post<T>(
    host: string,
    path: string,
    body: any,
    options?: HttpOptions | HttpAttachmentOptions
  ): Observable<T> {
    return this.postRequest(this.url(host, path), body, null, options);
  }

  public put(
    host: string,
    path: string,
    body: any,
    entitySchemaVersion: number,
    options: HttpOptions
  ): Observable<object> {
    const url = this.url(host, path);
    return this.httpClient
      .put(url, body, this.mergeOptions(entitySchemaVersion, options))
      .pipe(
        catchError((err) => {
          console.error(
            'An error occurred with the PUT request: ' + JSON.stringify(err)
          );
          return throwError(() => err);
        })
      );
  }

  private deleteRequest(
    url: string,
    entitySchemaVersion: number | null,
    options?: HttpOptions
  ): Observable<object> {
    return this.httpClient
      .delete(url, this.mergeOptions(entitySchemaVersion, options))
      .pipe(
        catchError((err) => {
          console.error(
            'An error occurred with the DELETE request: ' + JSON.stringify(err)
          );
          return throwError(() => err);
        })
      );
  }

  public deleteEntity(
    host: string,
    path: string,
    entitySchemaVersion: number,
    options?: HttpOptions
  ): Observable<object> {
    return this.deleteRequest(
      this.url(host, path),
      entitySchemaVersion,
      options
    );
  }

  public delete(
    host: string,
    path: string,
    options?: HttpOptions
  ): Observable<object> {
    return this.deleteRequest(this.url(host, path), null, options);
  }

  // { observe: 'body' } is a hint to TypeScript to use the appropriate http method override
  private mergeOptions(
    entitySchemaVersion: number | null,
    options?: any
  ): { observe: 'body' } {
    if (!options) {
      options = {};
    }
    options.headers = this.mergeHeaders(entitySchemaVersion, options.headers);
    return options;
  }

  private mergeHeaders(
    entitySchemaVersion: number | null,
    headers?: any
  ): HttpHeaders {
    if (!headers) {
      headers = {};
    }

    if (!(headers instanceof HttpHeaders)) {
      headers = new HttpHeaders(headers);
    }

    if (entitySchemaVersion != null) {
      return headers.set('X-EntitySchemaVersion', String(entitySchemaVersion));
    }

    return headers;
  }

  url = (host: string, path: string): string => `${host}${path}`;
}
